import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme, pageTitleStyle, pageTitleTextStyle, primaryButtonStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const ORANGE = theme.orange;
const BLACK  = theme.black;

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

const ROLES = ['manager','souscripteur','accounts','it','courtier','client','voyageur'];

const ROLE_STYLE = {
  manager:      { bg:'#FAEEDA', color:'#854F0B' },
  souscripteur: { bg:'#FEF3E2', color:'#9A5A00' },
  accounts:     { bg:'#E1F5EE', color:'#0F6E56' },
  it:           { bg:'#161616', color:'#fff'    },
  courtier:     { bg:'#E6F1FB', color:'#185FA5' },
  client:       { bg:'#EEEDFE', color:'#534AB7' },
  voyageur:     { bg:'#F1F1EE', color:'#5F5E5A' },
};

const STATUT_STYLE = {
  actif:    { bg:'#E1F5EE', color:'#0F6E56', dot:'#1D9E75' },
  inactif:  { bg:'#FCEBEB', color:'#A32D2D', dot:'#E24B4A' },
};

const initials = nom => (nom || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export default function UtilisateursList() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [items, setItems]     = useState([]);
  const [search, setSearch]   = useState('');
  const [role, setRole]       = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [searchFocus, setSearchFocus] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (role)   params.set('role', role);
      const r = await authFetch(`${BASE}/utilisateurs?${params}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      setItems(data);
      setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [search, role]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) {
    try {
      const r = await authFetch(`${BASE}/utilisateurs/${id}`, { method:'DELETE' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      setConfirm(null); load();
    } catch (e) { alert(e.message); setConfirm(null); }
  }

  async function handleReset(id, nom) {
    try {
      const r = await authFetch(`${BASE}/utilisateurs/${id}/reset-password`, { method:'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      setResetInfo({ nom, simulated: data.email_simulated });
      setTimeout(() => setResetInfo(null), 5000);
    } catch (e) { alert(e.message); }
  }

  return (
    <div>
      {/* En-tête : titre souligné (style "Ligne") */}
      <div style={{ ...pageTitleStyle, marginBottom:16 }}>
        <span style={pageTitleTextStyle}>
          <SvgIcon size={22} color={ORANGE}><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/></SvgIcon>
          {t('utilisateurs.title')}
        </span>
        <span style={{ fontSize:12, color:theme.textTertiary }}>
          {items.length} {items.length !== 1 ? t('utilisateurs.count_plural') : t('utilisateurs.count')}
        </span>
        <button onClick={() => navigate('/utilisateurs/nouveau')}
          style={{ ...primaryButtonStyle, marginLeft:'auto' }}>
          {t('utilisateurs.create_btn')}
        </button>
      </div>

      {/* Recherche + filtres rôles */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:220, display:'flex', alignItems:'center', gap:8,
          border:`1px solid ${searchFocus ? ORANGE : '#D9D9D5'}`, borderRadius:8, padding:'0 12px', background:'#fff',
          boxShadow: searchFocus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none', transition:'all .12s' }}>
          <SvgIcon size={16} color="#A0A0A0"><path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0M21 21l-6 -6"/></SvgIcon>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onFocus={()=>setSearchFocus(true)} onBlur={()=>setSearchFocus(false)}
            placeholder={t('utilisateurs.search_placeholder')}
            style={{ flex:1, border:'none', outline:'none', padding:'10px 0', fontSize:14, fontFamily:'inherit', background:'transparent' }}/>
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          <button onClick={()=>setRole('')}
            style={{ fontSize:13, padding:'9px 14px', borderRadius:8, cursor:'pointer', fontWeight: role===''?500:400,
              background: role===''?BLACK:'#fff', color: role===''?'#fff':'#64748B',
              border: role===''?'none':'1px solid #D9D9D5' }}>
            {t('utilisateurs.filter_all')}
          </button>
          {ROLES.map(r => (
            <button key={r} onClick={()=>setRole(r)}
              style={{ fontSize:13, padding:'9px 14px', borderRadius:8, cursor:'pointer', fontWeight: role===r?500:400,
                background: role===r?BLACK:'#fff', color: role===r?'#fff':'#64748B',
                border: role===r?'none':'1px solid #D9D9D5' }}>
              {t('nav.role_'+r)}
            </button>
          ))}
        </div>
      </div>

      {/* Confirmation reset password */}
      {resetInfo && (
        <div style={{ background:'#E1F5EE', border:'1px solid #B6E5D2', borderRadius:8, padding:'12px 16px', marginBottom:12, fontSize:13, color:'#0F6E56', display:'flex', alignItems:'center', gap:9 }}>
          <SvgIcon size={18} color="#0F6E56"><path d="M5 12l5 5l10 -10"/></SvgIcon>
          {t('utilisateurs.reset_sent', { nom: resetInfo.nom }) || `Lien de réinitialisation envoyé à ${resetInfo.nom}`}
          {resetInfo.simulated && <span style={{ marginLeft:'auto', fontSize:11, fontStyle:'italic', color:'#5F5E5A' }}>(mode simulé — vérifier la console)</span>}
        </div>
      )}

      {error && <div style={{ color:'#A32D2D', marginBottom:12, fontSize:13 }}>⚠ {error}</div>}

      {loading ? <div style={{ color:'#64748B', padding:24 }}>{t('common.loading')}</div> : (
        <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
          {items.map((u, idx) => {
            const rs = ROLE_STYLE[u.role] || ROLE_STYLE.voyageur;
            const ss = STATUT_STYLE[u.statut] || STATUT_STYLE.inactif;
            return (
              <div key={u.id}>
                <div onClick={() => navigate(`/utilisateurs/${u.id}`)}
                  style={{ display:'flex', alignItems:'center', gap:13, padding:'14px 18px', cursor:'pointer',
                    borderBottom: idx < items.length - 1 ? '0.5px solid #F1F1EE' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background='#FAFAF8'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <div style={{ width:38, height:38, borderRadius:9, flexShrink:0,
                    background: u.statut === 'actif' ? '#FEF3E2' : '#F1F1EE',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:13, fontWeight:500, color: u.statut === 'actif' ? '#9A5A00' : '#64748B' }}>
                    {initials(u.nom)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:'#1A1A1A' }}>{u.nom || '—'}</div>
                    <div style={{ fontSize:12, color:'#64748B', marginTop:1 }}>
                      {u.email}
                      {u.client_nom && ` · ${u.client_nom}`}
                    </div>
                  </div>
                  <span style={{ background: rs.bg, color: rs.color, padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:500 }}>
                    {t('nav.role_'+u.role) || u.role}
                  </span>
                  <span style={{ background: ss.bg, color: ss.color, padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:500,
                    display:'inline-flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:ss.dot }}/>
                    {u.statut === 'actif' ? t('utilisateurs.statut_actif') : t('utilisateurs.statut_inactif')}
                  </span>
                  <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleReset(u.id, u.nom)} title={t('utilisateurs.reset_password')}
                      style={{ width:30, height:30, borderRadius:7, cursor:'pointer', background:'#FEF3E2', border:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <SvgIcon size={15} color="#9A5A00"><path d="M15 8h.01M6 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0M9 13l-6 6l3 0v3l3 0l3 -3l-3 -3M21 7a4 4 0 0 0 -8 0a4 4 0 0 0 8 0"/></SvgIcon>
                    </button>
                    <button onClick={() => navigate(`/utilisateurs/${u.id}`)} title={t('common.modify')}
                      style={{ width:30, height:30, borderRadius:7, cursor:'pointer', background:'#F7F7F5', border:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <SvgIcon size={15} color="#64748B"><path d="M9 7h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3M16 5l3 3"/></SvgIcon>
                    </button>
                    <button onClick={() => setConfirm({ id: u.id, nom: u.nom })} title={t('common.delete')}
                      style={{ width:30, height:30, borderRadius:7, cursor:'pointer', background:'#FCEBEB', border:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <SvgIcon size={15} color="#A32D2D"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></SvgIcon>
                    </button>
                  </div>
                </div>
                {confirm?.id === u.id && (
                  <div style={{ background:'#FCEBEB', borderTop:'0.5px solid #F09595', padding:'12px 18px', fontSize:13 }}>
                    {t('common.delete')} <strong>{confirm.nom}</strong> ?
                    <span style={{ display:'inline-flex', gap:6, marginLeft:12 }}>
                      <button onClick={() => handleDelete(u.id)} style={{ background:'#A32D2D', color:'#fff', border:'none', borderRadius:6, padding:'5px 13px', cursor:'pointer', fontSize:12, fontWeight:500 }}>{t('common.yes')}</button>
                      <button onClick={() => setConfirm(null)} style={{ border:'1px solid #CBD5E1', borderRadius:6, padding:'5px 13px', cursor:'pointer', background:'#fff', fontSize:12 }}>{t('common.cancel')}</button>
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <div style={{ padding:32, textAlign:'center', color:'#94A3B8', fontSize:14 }}>{t('common.no_results')}</div>}
        </div>
      )}
    </div>
  );
}
